'use client';
import {
  AssessmentQuestion,
  AssessmentRecord,
  AssessmentSection,
  AssessmentSubmission,
} from '@scaleup/models';
import { FC, useEffect, useState } from 'react';
import {
  useGetAssessmentSubmissionQuery,
  useUpdateAssessmentSubmissionMutation,
} from '../../api';
import { UnstyledModal } from '../../ui';
import { Confirmation } from './Confirmation';
import { SectionIntro } from './SectionIntro';
import { SectionQuestions } from './SectionQuestions';

type IParams = {
  readonly showModal: boolean;
  readonly closeModal: () => void;
  readonly section: AssessmentSection | null;
  submission: AssessmentSubmission | undefined;
  setSubmission: (value: AssessmentSubmission) => void;
  readonly loading: boolean;
  readonly submissionLoading: boolean;
};

function getQuestionCount(section: AssessmentSection | null): number {
  if (!section) return 0;
  return section.questions.length;
}

function getRemainingQuestions(
  section: AssessmentSection | null,
  submission: AssessmentSubmission | undefined
): number {
  if (!section) return 0;
  if (!submission) return section.questions.length;
  let count = 0;
  section.questions.forEach((question) => {
    let answer = submission.answers[section.slug]?.[`${question.id}`]?.answer;
    if (answer == null || answer == undefined || answer == '') count++;
  });
  return count;
}

export const useAssessmentModal = (assessmentRecord: AssessmentRecord) => {
  const [section, setSection] = useState<AssessmentSection>();
  const [show, setShow] = useState(false);
  const [answers, setAnswers] = useState<AssessmentSubmission | undefined>();

  const { data: submission, isLoading: isSubmissionLoading } =
    useGetAssessmentSubmissionQuery(assessmentRecord.assessmentRecordId);

  const [updateSubmission, { isLoading: isUpdateSubmissionLoading }] =
    useUpdateAssessmentSubmissionMutation();

  // if we have a current submission set the current answers to the existing submission
  useEffect(() => {
    if (submission) {
      setAnswers({ ...submission });
    }
  }, [submission]);

  // update the submission with the update answers when they change
  useEffect(() => {
    if (answers && submission && (JSON.stringify(answers) !== JSON.stringify(submission))) {
      updateSubmission({
        submissionId: submission!.submissionId,
        submission: answers,
      });
    }
  }, [answers, submission, updateSubmission]);

  const closeModal = () => {
    setShow(false);
    setSection(undefined);
  };

  const renderAssessmentModal = () => {
    if (!section || !show) {
      return null;
    }

    return (
      <AssessmentModal
        loading={isSubmissionLoading}
        submissionLoading={isUpdateSubmissionLoading}
        submission={answers}
        showModal={show}
        section={section}
        closeModal={closeModal}
        setSubmission={setAnswers}
      />
    );
  };

  const showAssessmentModal = (_section: AssessmentSection) => {
    setSection(_section);
    setShow(true);
  };

  const getAssessmentSectionStatus = (
    section: AssessmentSection
  ): 'pending' | 'inProgress' | 'completed' => {
    if (!answers) {
      return 'pending';
    }
    let count = 0;
    const _answers = answers.answers;
    const sectionAnswers = _answers[section.slug];
    if (!sectionAnswers) {
      return 'pending';
    }
    section.questions.forEach((question: AssessmentQuestion) => {
      const questionAnswer = sectionAnswers[question.id];
      if (questionAnswer && questionAnswer.answer.length > 0) {
        count++;
      }
    });

    if (count === section.questions.length) {
      return 'completed';
    }

    if (count > 0) {
      return 'inProgress';
    }

    return 'pending';
  };

  const getAssessmentAnsweredStatus = (): boolean => {
    if (!answers) {
      return false;
    }
    // if any section is not completed return false
    let completed = true;
    Object.keys(answers.sectionStatus).forEach((key) => {
      completed = completed && (answers.sectionStatus[key] || false);
    });
    
    return completed;
  };

  const getAssessmentCompletionStatus = (): boolean => {
    if (!answers) {
      return false;
    }
    return answers.completed || false;
  };

  const getCurrentSubmissionId = (): string | null => {
    if (!answers) {
      return null;
    }
    return answers.submissionId || null;
  }

  return {
    renderAssessmentModal,
    showAssessmentModal,
    getAssessmentSectionStatus,
    getAssessmentCompletionStatus,
    getAssessmentAnsweredStatus,
    getCurrentSubmissionId
  };
};

export const AssessmentModal: FC<IParams> = ({
  showModal,
  closeModal,
  section,
  submission,
  setSubmission,
  loading,
  submissionLoading,
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [sectionComplete, setSectionComplete] = useState(false);
  const handleChangeValue = (key: string, value: string | Array<string>) => {};

  const handleCloseModal = () => {
    setCurrentPage(0);
    setSectionComplete(false);
    closeModal();
  };

  return (
    <>
      <UnstyledModal
        title={
          sectionComplete
            ? ''
            : currentPage === 0
              ? 'Scaleup Readiness Assessment'
              : `${section?.section}`
        }
        showModal={showModal}
        loading={loading}
        handleCloseModal={handleCloseModal}
        confirmation={true}
      >
        {currentPage === 0 && section && !sectionComplete && (
          <SectionIntro
            description={section.overview}
            sectionName={section.section}
            subSections={Object.keys(section.subSections)}
            questionCount={getQuestionCount(section)}
            remainingQuestions={getRemainingQuestions(section, submission)}
            changePage={setCurrentPage}
          />
        )}
        {currentPage !== 0 && section && !sectionComplete && (
          <SectionQuestions
            currentPage={currentPage}
            submission={submission}
            submissionLoading={submissionLoading}
            setSubmission={setSubmission}
            section={section}
            changePage={setCurrentPage}
            sectionComplete={setSectionComplete}
          />
        )}
        {sectionComplete && section && (
          <Confirmation
            handleCloseModal={handleCloseModal}
            section={section.section}
          />
        )}
      </UnstyledModal>
    </>
  );
};
